import React, { useEffect, useState, useMemo } from "react";
import AdminDashboardLayout from "../../pages/superadmin/AdminDashboardLayout";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCorporatesAPI, UpdateStatusAPI } from "../../redux/slices/superAdminCorporateSlice";
import { errorHandler, base } from "../../constants/base";
import { toast } from "react-toastify";
import AdminDataTable from "../../components/AdminComponents/AdminDataTable";
import "./ModuleAnalytics.css";

const SuperAdminCompanies = () => {
    const dispatch = useDispatch();
    const loading = useSelector(state => state.SuperAdminCorporateSlice.loading);
    const corporates = useSelector((state) => state?.SuperAdminCorporateSlice?.corporates);
    const analytics = useSelector((state) => state?.SuperAdminCorporateSlice?.analytics);
    const pagination = useSelector((state) => state?.SuperAdminCorporateSlice?.pagination);
    const [currentPage, setCurrentPage] = useState(pagination?.current_page || 1);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchValue, setSearchValue] = useState('');

    const fetchCorporates = (page = currentPage, search = searchValue, status = statusFilter) => {
        const payload = {
            page: page,
            search: search,
            status: status
        }
        dispatch(getCorporatesAPI(payload));
    };

    useEffect(() => {
        fetchCorporates(currentPage, searchValue, statusFilter);
    }, [currentPage, statusFilter]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    }

    const handleSearch = (value) => {
        setSearchValue(value);
        setCurrentPage(1);
        fetchCorporates(1, value, statusFilter);
    }

    const handleStatusFilter = (e) => {
        setStatusFilter(e.target.value);
        setCurrentPage(1);
    }

    const handleChangeStatus = async (id, newStatus) => {
        const statusUpdate = {
            id: id,
            status: newStatus,
        }

        const res = await dispatch(UpdateStatusAPI(statusUpdate))

        if (res.type === 'UpdateStatusAPI/fulfilled') {
            if (res.payload.success || res.payload.status || res.payload.data.status === 200) {
                fetchCorporates(currentPage, searchValue, statusFilter);
                toast.success("Corporate Status Updated Successfully");
            } else {
                errorHandler(res.payload.errors);
            }
        } else if (res.type === 'UpdateStatusAPI/rejected') {
            errorHandler(res.payload.errors);
        } else {
            toast.error('Sorry, something went wrong!');
        }
    };

    // Define columns for DataTable
    const columns = useMemo(() => [
        {
            Header: 'Corporate',
            accessor: 'name',
            Cell: ({ row }) => (
                <div className="admin-dt-user-cell">
                    {row.original?.images?.[0]?.path ? (
                        <img
                            src={`${base.imageUrl}${row.original.images[0].path}`}
                            alt={row.original.name}
                            className="admin-dt-user-avatar"
                        />
                    ) : (
                        <div className="admin-dt-user-placeholder">
                            <i className="fas fa-building"></i>
                        </div>
                    )}
                    <div className="admin-dt-user-info">
                        <p className="admin-dt-user-name">{row.original?.name}</p>
                        <p className="admin-dt-user-email">{row.original?.email}</p>
                    </div>
                </div>
            ),
        },
        {
            Header: 'Phone',
            accessor: 'phone_no',
            className: 'text-center',
            Cell: ({ value }) => value || '-',
        },
        {
            Header: 'Employees',
            accessor: 'employees',
            className: 'text-center',
            disableSortBy: true,
            Cell: ({ row }) => (
                <Link to={`/admin/employees/${row.original?.id}`}>
                    <span className="admin-dt-btn">
                        <i className="fas fa-users"></i> View
                    </span>
                </Link>
            ),
        },
        {
            Header: 'Status',
            accessor: 'status',
            className: 'text-center',
            disableSortBy: true,
            Cell: ({ row }) => (
                <select
                    value={row.original?.status}
                    onChange={(e) => handleChangeStatus(row.original?.id, e.target.value)}
                    className="admin-dt-select"
                >
                    <option value="Pending">Pending</option>
                    <option value="Active">Active</option>
                    <option value="Block">Block</option>
                </select>
            ),
        },
        {
            Header: 'Actions',
            accessor: 'id',
            className: 'text-center',
            disableSortBy: true,
            Cell: ({ value }) => (
                <div className="admin-dt-actions">
                    <Link to={`/admin/corporate/view/${value}`} className="admin-dt-action-btn view">
                        <i className="fas fa-eye"></i>
                    </Link>
                </div>
            ),
        },
    ], []);

    const analyticsCards = [
        { title: 'Total Corporates', value: analytics?.total || 0, icon: 'fa-solid fa-building', color: '#EF4444' },
        { title: 'Active', value: analytics?.active || 0, icon: 'fa-solid fa-building-circle-check', color: '#60A5FA' },
        { title: 'Pending', value: analytics?.pending || 0, icon: 'fa-solid fa-building-circle-exclamation', color: '#4ADE80' },
        { title: 'Blocked', value: analytics?.blocked || 0, icon: 'fa-solid fa-building-circle-xmark', color: '#FBBF24' },
    ];

    const filterElements = (
        <select value={statusFilter} onChange={handleStatusFilter} className="admin-dt-filter-select">
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Active">Active</option>
            <option value="Block">Blocked</option>
        </select>
    );

    return (
        <AdminDashboardLayout>
            {/* Analytics Cards */}
            <div className="module-analytics-grid">
                {analyticsCards.map((card, index) => (
                    <div key={index} className="module-analytics-card">
                        <div className="module-analytics-icon" style={{ backgroundColor: `${card.color}15`, color: card.color }}>
                            <i className={card.icon}></i>
                        </div>
                        <div className="module-analytics-content">
                            <p className="module-analytics-label">{card.title}</p>
                            <h3 className="module-analytics-value">{card.value}</h3>
                        </div>
                    </div>
                ))}
            </div>

            <AdminDataTable
                columns={columns}
                data={corporates}
                title="Corporates"
                icon="fa-solid fa-building"
                loading={loading}
                searchable={true}
                searchPlaceholder="Search corporates..."
                onSearch={handleSearch}
                filters={filterElements}
                serverSidePagination={{
                    currentPage: pagination?.current_page || 1,
                    totalPages: pagination?.total_pages || 1,
                    total: pagination?.total || 0,
                    perPage: pagination?.per_page || 10,
                    onPageChange: handlePageChange,
                }}
            />
        </AdminDashboardLayout>
    );
};

export default SuperAdminCompanies;
